import React, { useEffect, useState } from 'react';
import { Input, Tree } from 'antd';
import { DownOutlined, HomeOutlined } from '@ant-design/icons';
import type { DataNode, TreeProps } from 'antd/es/tree';
import { useQuery } from '@apollo/client';
import { GET_ALL_ACCESS_HUB_AND_WAREHOUSES_FOR_ROLE } from 'graphql/queries/getAllAccessHubAndWarehousesForRole';
import { WarehouseType } from 'graphql/types/WarehouseType';
import { SiteType } from 'graphql/types/SiteType';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'antd';

const { Search } = Input;

interface SitesMapTreeProps {
    onCheckboxChange?: (checkedIds: Set<string>, warehouses: WarehouseType[]) => void;
    onWarehousesChange?: (warehouses: WarehouseType[]) => void;
    getColorForSite?: (site: WarehouseType) => string;
}

// Hub node title component with checkbox
const HubTreeTitle = ({ 
    hub, 
    checked, 
    onCheck,
    color,
    isPartialChecked
}: { 
    hub: WarehouseType;
    checked: boolean;
    onCheck: (checked: boolean) => void;
    color?: string;
    isPartialChecked?: boolean; // 状态3：只选中 Hub，不选中 Warehouse
}) => (
    <div className="flex items-center w-full min-w-0">
        <Checkbox
            checked={checked || isPartialChecked}
            onChange={(e) => {
                e.stopPropagation();
                onCheck(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0 mr-2 custom-checkbox-color"
            style={{
                '--ant-checkbox-color': (color && checked) ? color : '#ffffff',
            } as React.CSSProperties}
        />
        <div className="flex items-center min-w-0 flex-1">
            <span className="font-medium truncate">{hub.name}</span>
            <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">({hub.sortingCode})</span>
        </div>
    </div>
);

// Warehouse node title component with checkbox
const WarehouseTreeTitle = ({ 
    warehouse, 
    checked, 
    onCheck,
    color
}: { 
    warehouse: WarehouseType;
    checked: boolean;
    onCheck: (checked: boolean) => void;
    color?: string;
}) => (
    <div className="flex items-center w-full min-w-0">
        <Checkbox
            checked={checked}
            onChange={(e) => {
                e.stopPropagation();
                onCheck(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0 mr-2 custom-checkbox-color"
            style={{
                '--ant-checkbox-color': (color && checked) ? color : '#ffffff',
            } as React.CSSProperties}
        />
        <div className="flex items-center min-w-0 flex-1">
            <HomeOutlined className="mr-2 flex-shrink-0" />
            <span className="truncate">{warehouse.name}</span>
            <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">({warehouse.sortingCode})</span>
        </div>
    </div>
);

// GraphQL query hook
const useGetAllAccessHubAndWarehousesForRole = () => {
    const { data, refetch } = useQuery(GET_ALL_ACCESS_HUB_AND_WAREHOUSES_FOR_ROLE);
    return {
        allWarehouses: data?.warehouses || [],
        refetch
    };
};

const SitesMapTree: React.FC<SitesMapTreeProps> = ({
    onCheckboxChange,
    onWarehousesChange,
    getColorForSite
}) => {
    const { t } = useTranslation();

    // GraphQL hooks
    const { allWarehouses } = useGetAllAccessHubAndWarehousesForRole();

    // 通知父组件所有 warehouse 数据变化
    useEffect(() => {
        if (allWarehouses?.length > 0 && onWarehousesChange) {
            const warehouses = allWarehouses.map((w: any) => ({
                ...w,
                id: Number(w.id),
                parentSiteId: Number(w.parentSiteId)
            }));
            onWarehousesChange(warehouses);
        }
    }, [allWarehouses, onWarehousesChange]);

    // State
    const [treeData, setTreeData] = useState<DataNode[]>([]);
    const [filteredTreeData, setFilteredTreeData] = useState<DataNode[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');
    const [checkedWarehouseIds, setCheckedWarehouseIds] = useState<Set<string>>(new Set());
    const [checkedHubIds, setCheckedHubIds] = useState<Set<string>>(new Set());
    const [warehouseMap, setWarehouseMap] = useState<Map<string, WarehouseType>>(new Map());

    // Build warehouse map
    useEffect(() => {
        if (allWarehouses?.length > 0) {
            const warehouses = allWarehouses.map((w: any) => ({
                ...w,
                id: Number(w.id),
                parentSiteId: Number(w.parentSiteId)
            }));

            // 创建 warehouse ID 到 warehouse 对象的映射
            const newWarehouseMap = new Map<string, WarehouseType>();
            warehouses.forEach((w: any) => {
                if (w.siteType === SiteType.WAREHOUSE && w.id) {
                    newWarehouseMap.set(w.id.toString(), w);
                }
            });
            setWarehouseMap(newWarehouseMap);
        }
    }, [allWarehouses]);

    // Build tree data
    useEffect(() => {
        if (allWarehouses?.length > 0) {
            const warehouses = allWarehouses.map((w: any) => ({
                ...w,
                id: Number(w.id),
                parentSiteId: Number(w.parentSiteId)
            }));

            const hubMap = new Map<number, { hub: WarehouseType; warehouses: WarehouseType[] }>();

            // Group warehouses by hub
            warehouses.forEach((warehouse: any) => {
                if (warehouse.siteType === SiteType.HUB) {
                    hubMap.set(warehouse.id || 0, { hub: warehouse, warehouses: [] });
                }
            });

            warehouses.forEach((warehouse: any) => {
                if (warehouse.siteType === SiteType.WAREHOUSE) {
                    const hub = hubMap.get(warehouse.parentSiteId || 0);
                    if (hub) {
                        hub.warehouses.push(warehouse);
                    }
                }
            });

            // Sort hubs/warehouses alphabetically by name (case-insensitive, full string comparison),
            // with stable fallback to sortingCode then id to keep ordering deterministic.
            const compareByName = (
                a?: { name?: string; sortingCode?: string; id?: number },
                b?: { name?: string; sortingCode?: string; id?: number }
            ) => {
                const aName = (a?.name || '').trim();
                const bName = (b?.name || '').trim();
                if (aName && bName) {
                    const cmp = aName.localeCompare(bName, undefined, { sensitivity: 'base', numeric: true });
                    if (cmp !== 0) return cmp;
                } else if (aName || bName) {
                    return aName ? -1 : 1;
                }

                const aCode = (a?.sortingCode || '').trim();
                const bCode = (b?.sortingCode || '').trim();
                if (aCode && bCode) {
                    const cmp = aCode.localeCompare(bCode, undefined, { sensitivity: 'base', numeric: true });
                    if (cmp !== 0) return cmp;
                } else if (aCode || bCode) {
                    return aCode ? -1 : 1;
                }

                return Number(a?.id || 0) - Number(b?.id || 0);
            };

            const sortedHubs = Array.from(hubMap.values()).sort((a, b) => compareByName(a.hub, b.hub));
            sortedHubs.forEach(group => {
                group.warehouses.sort((a, b) => compareByName(a, b));
            });

            const treeNodes: DataNode[] = sortedHubs.map(hub => {
                const hubId = hub.hub.id?.toString() || '';
                const isHubChecked = checkedHubIds.has(hubId);
                
                // 获取该 Hub 下的所有 Warehouse ID
                const hubWarehouseIds = hub.warehouses
                    .map(w => w.id?.toString())
                    .filter((id): id is string => id !== undefined);
                
                // 检查该 Hub 下的所有 Warehouse 是否都被选中
                const allWarehousesChecked = hubWarehouseIds.length > 0 && 
                    hubWarehouseIds.every(id => checkedWarehouseIds.has(id));
                
                // 判断 Hub checkbox 的显示状态
                // 如果 Hub 选中且所有 Warehouse 都选中，显示为选中状态
                const hubCheckboxChecked = isHubChecked && allWarehousesChecked;
                // 状态3：只选中 Hub，不选中 Warehouse
                const isPartialChecked = isHubChecked && !allWarehousesChecked;
                // 获取 Hub 的颜色
                const hubColor = getColorForSite ? getColorForSite(hub.hub) : undefined;
                
                return {
                    key: hubId,
                    title: (
                        <HubTreeTitle
                            hub={hub.hub}
                            checked={hubCheckboxChecked}
                            isPartialChecked={isPartialChecked}
                            color={hubColor}
                            onCheck={() => {
                                // 三态切换逻辑
                                if (!isHubChecked) {
                                    // 状态1 -> 状态2: 未选中 -> 选中 Hub + 所有 Warehouse
                                    const newCheckedHubIds = new Set(checkedHubIds);
                                    newCheckedHubIds.add(hubId);
                                    
                                    const newCheckedWarehouseIds = new Set(checkedWarehouseIds);
                                    hubWarehouseIds.forEach(id => {
                                        newCheckedWarehouseIds.add(id);
                                    });
                                    
                                    setCheckedHubIds(newCheckedHubIds);
                                    setCheckedWarehouseIds(newCheckedWarehouseIds);
                                    
                                    // 通知父组件
                                    const allWarehousesList = allWarehouses.map((w: any) => ({
                                        ...w,
                                        id: Number(w.id),
                                        parentSiteId: Number(w.parentSiteId)
                                    }));
                                    const selectedHubs = Array.from(newCheckedHubIds)
                                        .map(id => {
                                            const h = allWarehousesList.find((w: any) => 
                                                w.siteType === SiteType.HUB && w.id?.toString() === id
                                            );
                                            return h;
                                        })
                                        .filter((h): h is WarehouseType => h !== undefined);
                                    const selectedWarehouses = Array.from(newCheckedWarehouseIds)
                                        .map(id => {
                                            const w = allWarehousesList.find((w: any) => 
                                                w.siteType === SiteType.WAREHOUSE && w.id?.toString() === id
                                            );
                                            return w;
                                        })
                                        .filter((w): w is WarehouseType => w !== undefined);
                                    
                                    const allSelected = [...selectedHubs, ...selectedWarehouses];
                                    onCheckboxChange?.(new Set([...newCheckedHubIds, ...newCheckedWarehouseIds]), allSelected);
                                } else if (allWarehousesChecked) {
                                    // 状态2 -> 状态3: Hub + 所有 Warehouse 选中 -> 只选中 Hub
                                    const newCheckedWarehouseIds = new Set(checkedWarehouseIds);
                                    hubWarehouseIds.forEach(id => {
                                        newCheckedWarehouseIds.delete(id);
                                    });
                                    
                                    setCheckedWarehouseIds(newCheckedWarehouseIds);
                                    
                                    // 通知父组件
                                    const allWarehousesList = allWarehouses.map((w: any) => ({
                                        ...w,
                                        id: Number(w.id),
                                        parentSiteId: Number(w.parentSiteId)
                                    }));
                                    const selectedHubs = Array.from(checkedHubIds)
                                        .map(id => {
                                            const h = allWarehousesList.find((w: any) => 
                                                w.siteType === SiteType.HUB && w.id?.toString() === id
                                            );
                                            return h;
                                        })
                                        .filter((h): h is WarehouseType => h !== undefined);
                                    const selectedWarehouses = Array.from(newCheckedWarehouseIds)
                                        .map(id => {
                                            const w = allWarehousesList.find((w: any) => 
                                                w.siteType === SiteType.WAREHOUSE && w.id?.toString() === id
                                            );
                                            return w;
                                        })
                                        .filter((w): w is WarehouseType => w !== undefined);
                                    
                                    const allSelected = [...selectedHubs, ...selectedWarehouses];
                                    onCheckboxChange?.(new Set([...checkedHubIds, ...newCheckedWarehouseIds]), allSelected);
                                } else {
                                    // 状态3 -> 状态1: 只选中 Hub -> 取消选中
                                    const newCheckedHubIds = new Set(checkedHubIds);
                                    newCheckedHubIds.delete(hubId);
                                    
                                    setCheckedHubIds(newCheckedHubIds);
                                    
                                    // 通知父组件
                                    const allWarehousesList = allWarehouses.map((w: any) => ({
                                        ...w,
                                        id: Number(w.id),
                                        parentSiteId: Number(w.parentSiteId)
                                    }));
                                    const selectedHubs = Array.from(newCheckedHubIds)
                                        .map(id => {
                                            const h = allWarehousesList.find((w: any) => 
                                                w.siteType === SiteType.HUB && w.id?.toString() === id
                                            );
                                            return h;
                                        })
                                        .filter((h): h is WarehouseType => h !== undefined);
                                    const selectedWarehouses = Array.from(checkedWarehouseIds)
                                        .map(id => {
                                            const w = allWarehousesList.find((w: any) => 
                                                w.siteType === SiteType.WAREHOUSE && w.id?.toString() === id
                                            );
                                            return w;
                                        })
                                        .filter((w): w is WarehouseType => w !== undefined);
                                    
                                    const allSelected = [...selectedHubs, ...selectedWarehouses];
                                    onCheckboxChange?.(new Set([...newCheckedHubIds, ...checkedWarehouseIds]), allSelected);
                                }
                            }}
                        />
                    ),
                    isLeaf: false,
                    searchData: {
                        name: hub.hub.name || '',
                        sortingCode: hub.hub.sortingCode || ''
                    },
                    children: hub.warehouses.map(warehouse => {
                        const warehouseId = warehouse.id?.toString() || '';
                        const isWarehouseChecked = checkedWarehouseIds.has(warehouseId);
                        // 获取 Warehouse 的颜色
                        const warehouseColor = getColorForSite ? getColorForSite(warehouse) : undefined;
                        
                        return {
                            key: warehouseId,
                            isLeaf: true,
                            title: (
                                <WarehouseTreeTitle
                                    warehouse={warehouse}
                                    checked={isWarehouseChecked}
                                    color={warehouseColor}
                                    onCheck={(checked) => {
                                        const newCheckedIds = new Set(checkedWarehouseIds);
                                        if (checked) {
                                            newCheckedIds.add(warehouseId);
                                        } else {
                                            newCheckedIds.delete(warehouseId);
                                        }
                                        
                                        setCheckedWarehouseIds(newCheckedIds);
                                        
                                        // 通知父组件（从 allWarehouses 中获取选中的 warehouse 和 hub）
                                        const allWarehousesList = allWarehouses.map((w: any) => ({
                                            ...w,
                                            id: Number(w.id),
                                            parentSiteId: Number(w.parentSiteId)
                                        }));
                                        const selectedWarehouses = Array.from(newCheckedIds)
                                            .map(id => {
                                                const w = allWarehousesList.find((w: any) => 
                                                    w.siteType === SiteType.WAREHOUSE && w.id?.toString() === id
                                                );
                                                return w;
                                            })
                                            .filter((w): w is WarehouseType => w !== undefined);
                                        
                                        const selectedHubs = Array.from(checkedHubIds)
                                            .map(id => {
                                                const h = allWarehousesList.find((w: any) => 
                                                    w.siteType === SiteType.HUB && w.id?.toString() === id
                                                );
                                                return h;
                                            })
                                            .filter((h): h is WarehouseType => h !== undefined);
                                        
                                        const allSelected = [...selectedHubs, ...selectedWarehouses];
                                        onCheckboxChange?.(new Set([...checkedHubIds, ...newCheckedIds]), allSelected);
                                    }}
                                />
                            ),
                            searchData: {
                                name: warehouse.name || '',
                                sortingCode: warehouse.sortingCode || ''
                            }
                        };
                    })
                };
            });

            setTreeData(treeNodes);
        }
    }, [allWarehouses, checkedWarehouseIds, checkedHubIds, warehouseMap]);

    // Search filter effect
    useEffect(() => {
        const filtered = filterTreeData(treeData, searchValue);
        setFilteredTreeData(filtered);
    }, [treeData, searchValue]);

    const handleSearch = (value: string) => {
        setSearchValue(value);
        if (value) {
            const keysToExpand: React.Key[] = [];
            treeData.forEach(node => {
                if (node.children?.length) {
                    keysToExpand.push(node.key);
                }
            });
            setExpandedKeys(keysToExpand);
        }
    };

    const filterTreeData = (data: DataNode[], searchText: string): DataNode[] => {
        if (!searchText) return data;

        const searchLower = searchText.toLowerCase();

        return data.map(node => {
            // 检查 Hub 是否匹配
            const hubSearchData = (node as any).searchData;
            const hubMatches = hubSearchData && (
                hubSearchData.name.toLowerCase().includes(searchLower) ||
                hubSearchData.sortingCode.toLowerCase().includes(searchLower)
            );

            // 过滤子节点（Warehouses）
            const filteredChildren = node.children?.filter(child => {
                const warehouseSearchData = (child as any).searchData;
                return warehouseSearchData && (
                    warehouseSearchData.name.toLowerCase().includes(searchLower) ||
                    warehouseSearchData.sortingCode.toLowerCase().includes(searchLower)
                );
            }) || [];

            // 如果 Hub 匹配或有匹配的子节点，则包含此节点
            if (hubMatches || filteredChildren.length > 0) {
                return {
                    ...node,
                    children: hubMatches ? node.children : filteredChildren
                };
            }

            return null;
        }).filter(Boolean) as DataNode[];
    };

    return (
        <>
            <style>{`
                /* 自定义 checkbox 颜色样式 - 使用 CSS 变量设置颜色（选中时） */
                .custom-checkbox-color .ant-checkbox-checked .ant-checkbox-inner {
                    background-color: var(--ant-checkbox-color) !important;
                    border-color: var(--ant-checkbox-color) !important;
                }
                .custom-checkbox-color .ant-checkbox-checked::after {
                    border-color: var(--ant-checkbox-color) !important;
                }
                /* 当颜色为白色时，对勾使用黑色以确保可见 */
                .custom-checkbox-color[style*="--ant-checkbox-color: #ffffff"] .ant-checkbox-checked .ant-checkbox-inner::after,
                .custom-checkbox-color[style*="--ant-checkbox-color:#ffffff"] .ant-checkbox-checked .ant-checkbox-inner::after {
                    border-color: #000000 !important;
                }
                /* 未选中时的默认样式（白色背景） */
                .custom-checkbox-color .ant-checkbox:not(.ant-checkbox-checked) .ant-checkbox-inner {
                    background-color: #ffffff !important;
                    border-color: #d9d9d9 !important;
                }
            `}</style>
            <div className="border-r border-gray-200 p-4 flex flex-col gap-2" style={{ width: '400px', minWidth: '400px' }}>
                <Search
                    placeholder={t('central.sites.placeholders.searchHubWarehouse') as string}
                    className="mb-4"
                    allowClear
                    value={searchValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    onSearch={handleSearch}
                />

            <Tree
                showIcon
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                switcherIcon={<DownOutlined />}
                treeData={searchValue ? filteredTreeData : treeData}
                className="w-full"
            />
        </div>
        </>
    );
};

export default SitesMapTree;
